import "core-js/actual/array/at";
import "core-js/actual/array/find-last";
import "@/utils/browserCheck";

import { createApp } from "vue";
import App from "./App.vue";
import pinia from "./stores";
import router from "./router";
import { loadFonts } from "@/plugins/webfontloader";
import vuetify from "@/plugins/vuetify";
import rollbar from "@/plugins/rollbar";
import clarityPlugin from "@/plugins/microsoft-clarity";
import gaPlugin from "@/plugins/google-analytics";
import "flag-icons";
import "@/middleware/permission";
import { useFirebase } from "@/composables/useFirebase.composable";
import { usePushNotifications } from "@/composables/usePushNotifications.composable";
import { localeTranslator } from "@/utils/global.util";

loadFonts();

const app = createApp(App);

// Use Microsoft Clarity plugin
app.use(clarityPlugin, {
  enabled: Boolean(import.meta.env.APP_MICROSOFT_CLARITY_ID),
  projectId: import.meta.env.APP_MICROSOFT_CLARITY_ID,
});

// Use Google Analytics plugin
app.use(gaPlugin, {
  enabled: Boolean(import.meta.env.APP_GOOGLE_ANALYTICS_ID),
  id: import.meta.env.APP_GOOGLE_ANALYTICS_ID,
});

// Global error handler
app.config.errorHandler = (err) => {
  app.config.globalProperties.$rollbar?.error?.(err);
  if (window.gtag) {
    window.gtag("event", "error_displayed", {
      error_message: "GLOBAL_ERROR",
      error_code: err.status || err.code || "unknown",
    });
  }
  if (window.__crashlytics) {
    window.__crashlytics
      .recordException({
        message: err?.message || String(err),
        stack: err?.stack,
      })
      .catch(() => {});
  }
  throw err;
};

// Unhandled promise rejection → Crashlytics
window.addEventListener("unhandledrejection", (e) => {
  if (window.__crashlytics) {
    window.__crashlytics
      .recordException({
        message: String(e.reason?.message || e.reason),
      })
      .catch(() => {});
  }
});

// Inject global properties
app.config.globalProperties.$rollbar = rollbar;

// Use plugins
app.use(pinia);
app.use(router);
app.use(vuetify);

// Mount app
app.mount("#app");

// Request notification permission after webview is mounted (avoids race with native bridge ready)
async function autoRequestNotification() {
  const { Capacitor } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return;
  const platform = Capacitor.getPlatform();

  // iOS: the custom NotificationPermission plugin has no iOS implementation.
  // Skip it and let initPush() handle permission via @capacitor/push-notifications
  // (wraps UNUserNotificationCenter).
  if (platform === "ios") {
    void usePushNotifications().initPush();
    return;
  }

  // Android: custom plugin handles POST_NOTIFICATIONS runtime permission on API 33+.
  try {
    const { registerPlugin } = await import("@capacitor/core");
    const plugin = registerPlugin("NotificationPermission");
    const check = await plugin.checkPermission();
    if (check.receive === "prompt") {
      console.log("[notif] auto-requesting permission");
      const res = await plugin.requestPermission();
      console.log("[notif] auto-request result:", res.receive);
    } else {
      console.log("[notif] current status:", check.receive);
    }
  } catch (e) {
    console.warn("[notif] auto-request failed", e);
  } finally {
    // Always init push (token registration) regardless of permission outcome
    void usePushNotifications().initPush();
  }
}
void autoRequestNotification();

// Firebase: init analytics (web)
void useFirebase().initFirebase();

// Force update check (native only) — fire and forget, doesn't block mount.
// Reads Firebase Remote Config (app_version, force_update) and shows a
// blocking dialog via the forceUpdate store if the installed app is
// below the required minimum version.
async function checkForceUpdate() {
  try {
    const { useForceUpdate } = await import("@/composables/useForceUpdate.composable");
    await useForceUpdate().check();
  } catch (e) {
    console.warn("[force-update] init failed", e);
  }
}
void checkForceUpdate();

async function setupCrashlytics() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) return;
    const { Crashlytics } = await import("@capacitor-firebase/crashlytics");
    window.__crashlytics = Crashlytics;
  } catch (e) {
    console.warn("[crashlytics] setup failed", e);
  }
}
void setupCrashlytics();

// Firebase Analytics: track screen views (in addition to GA4 page_view in router)
router.afterEach((to) => {
  const fb = useFirebase();
  const title = to.meta?.text ? localeTranslator(to.meta.text) : to.name || document.title;
  fb.trackScreen(title, to.fullPath);
});
